import { Button } from "@/components/core/buttons/button";
import { useModal } from "@/components/core/modals/use-modal";
import { JournalAccountSelect } from "@/components/invoices/journal-account-select";
import { usePostInvoiceJournalEntryMutation } from "@/features/dashboard/api/queries/invoice-query";
import {
	invoiceRequestJournalItemsQueryOptions,
	useResetJournalLinesMutation,
	useUpdateJournalLinesMutation,
} from "@/features/dashboard/api/queries/invoice-request-query";
import type { InvoiceJournalLine } from "@/features/dashboard/types/invoice";
import { IconCheck, IconEdit, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

const phpFormat = (n: number) => n.toLocaleString("en-PH", { style: "currency", currency: "PHP" });

interface RequestJournalItemsTabProps {
	requestId: string;
}

export function RequestJournalItemsTab({ requestId }: RequestJournalItemsTabProps) {
	const { data, isLoading, isError } = useQuery(invoiceRequestJournalItemsQueryOptions(requestId));
	const invoiceId = data?.invoice_id ?? "";
	const postMutation = usePostInvoiceJournalEntryMutation(invoiceId);
	const updateMutation = useUpdateJournalLinesMutation(requestId);
	const resetMutation = useResetJournalLinesMutation(requestId);

	const modal = useModal();

	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [editedLine, setEditedLine] = useState<Partial<InvoiceJournalLine>>({});
	const [isAdding, setIsAdding] = useState(false);
	const [newLine, setNewLine] = useState<Partial<InvoiceJournalLine>>({
		account_code: "",
		account_name: "",
		description: "",
		debit: 0,
		credit: 0,
		tax_code: null,
		purpose: null,
	});

	if (isLoading) {
		return <div className="py-8 text-center text-sm text-gray-500">Loading journal items…</div>;
	}

	if (isError || !data) {
		return <div className="py-8 text-center text-sm text-gray-500">Unable to load journal items.</div>;
	}

	if (data.lines.length === 0) {
		return <div className="py-8 text-center text-sm text-gray-500">No journal items computed yet — set the request totals first.</div>;
	}

	const isPosted = data.status === "posted" && !!data.entry_number;
	const isBooksDraft = data.status === "draft" && !!data.entry_number;
	const isDraft = !isPosted;
	const hasInvoice = !!data.invoice_id;

	const handleEdit = (index: number) => {
		console.log("Edit button clicked for index:", index);
		setEditingIndex(index);
		setEditedLine({ ...data.lines[index] });
		console.log("Editing line:", data.lines[index]);
	};

	const handleCancelEdit = () => {
		setEditingIndex(null);
		setEditedLine({});
	};

	const handleSaveEdit = () => {
		if (editingIndex === null) return;

		// Merge the edited fields with the original line
		const updatedLine = {
			...data.lines[editingIndex],
			...editedLine,
		} as InvoiceJournalLine;

		// Validate the edited line
		if (!updatedLine.account_code) {
			toast.error("Please select an account");
			return;
		}
		if (Number(updatedLine.debit) === 0 && Number(updatedLine.credit) === 0) {
			toast.error("Please enter either a debit or credit amount");
			return;
		}

		const updatedLines = [...data.lines];
		updatedLines[editingIndex] = updatedLine;

		console.log("Saving edited lines:", updatedLines);
		updateMutation.mutate(updatedLines, {
			onSuccess: () => {
				toast.success("Journal line updated");
				setEditingIndex(null);
				setEditedLine({});
			},
			onError: (error: any) => {
				toast.error(error.response?.data?.message || "Failed to update journal line");
			},
		});
	};

	const handleDelete = (index: number) => {
		const updatedLines = data.lines.filter((_, i) => i !== index);
		if (updatedLines.length === 0) {
			modal.openModal({
				type: "confirm",
				variant: "red",
				title: "Delete All Lines",
				description: "This will delete all lines. Reset to auto-computed instead?",
				onConfirm: async () => {
					resetMutation.mutate(undefined, {
						onSuccess: () => toast.success("Reset to auto-computed lines"),
						onError: () => toast.error("Failed to reset lines"),
					});
				},
			});
		} else {
			updateMutation.mutate(updatedLines, {
				onSuccess: () => {
					toast.success("Journal line deleted");
				},
				onError: () => {
					toast.error("Failed to delete journal line");
				},
			});
		}
	};

	const handleAddLine = () => {
		if (!newLine.account_code || (!newLine.debit && !newLine.credit)) {
			toast.error("Please select an account and enter either a debit or credit amount");
			return;
		}
		const updatedLines = [
			...data.lines,
			{
				purpose: null,
				account_code: newLine.account_code!,
				account_name: newLine.account_name!,
				description: newLine.description || "",
				debit: Number(newLine.debit) || 0,
				credit: Number(newLine.credit) || 0,
				tax_code: newLine.tax_code || null,
			},
		];
		updateMutation.mutate(updatedLines, {
			onSuccess: () => {
				toast.success("Journal line added");
				setIsAdding(false);
				setNewLine({
					account_code: "",
					account_name: "",
					description: "",
					debit: 0,
					credit: 0,
					tax_code: null,
					purpose: null,
				});
			},
			onError: () => {
				toast.error("Failed to add journal line");
			},
		});
	};

	const handleReset = () => {
		modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Reset Journal Lines",
			description: "Reset to auto-computed journal lines? Your custom edits will be discarded.",
			onConfirm: async () => {
				resetMutation.mutate(undefined, {
					onSuccess: () => toast.success("Reset to auto-computed lines"),
					onError: () => toast.error("Failed to reset lines"),
				});
			},
		});
	};

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 text-xs text-gray-500">
				<div className="space-y-1">
					<div>
						JE number: <span className="font-mono text-gray-900">{data.entry_number ?? "— (draft)"}</span>
					</div>
					<div>
						Entry date: <span className="text-gray-900">{data.entry_date ?? "—"}</span>
					</div>
					{data.posted_at && (
						<div>
							Posted at: <span className="text-gray-900">{new Date(data.posted_at).toLocaleString("en-PH")}</span>
						</div>
					)}
				</div>
				<div className="flex flex-col items-end gap-2">
					<div className="flex items-center gap-2">
						<StatusBadge posted={isPosted} booksDraft={isBooksDraft} />
						{data.is_custom && <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-purple-800">Custom</span>}
						<span className={data.is_balanced ? "text-green-700" : "font-semibold text-red-600"}>{data.is_balanced ? "Balanced" : "Unbalanced"}</span>
					</div>
					<div className="flex gap-2">
						{isDraft && data.is_custom && (
							<Button variant="ghost" size="sm" onClick={handleReset} disabled={resetMutation.isPending}>
								{resetMutation.isPending ? "Resetting..." : "Reset"}
							</Button>
						)}
						{isDraft && !isAdding && editingIndex === null && (
							<Button variant="ghost" size="sm" onClick={() => setIsAdding(true)} disabled={updateMutation.isPending}>
								<IconPlus size={16} stroke={2} />
								<span>Add Item</span>
							</Button>
						)}
						{isDraft && hasInvoice && (
							<Button
								size="sm"
								onClick={() => postMutation.mutate("draft")}
								disabled={postMutation.isPending || !data.is_balanced}
								title={
									isBooksDraft
										? "Replace the draft in Books with the current local edits."
										: "Push the AR JE to Books as a draft. Posting to the GL happens inside Books."
								}
							>
								{postMutation.isPending ? "Sending…" : isBooksDraft ? "Re-send to Books" : "Send to Books"}
							</Button>
						)}
					</div>
					{isBooksDraft && (
						<span className="text-xs text-blue-700">
							Already in Books as <span className="font-mono">{data.entry_number}</span>. Post it from Books.
						</span>
					)}
				</div>
			</div>

			{data.last_error && (
				<div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">Last post attempt failed: {data.last_error}</div>
			)}

			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-gray-200">
							<th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase w-[30%]">Account</th>
							<th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Label</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase w-32">Debit</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase w-32">Credit</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase w-24">Tax</th>
							{isDraft && <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase w-24">Actions</th>}
						</tr>
					</thead>
					<tbody>
						{data.lines.map((line, index) => {
							const isEditing = editingIndex === index;
							return (
								<tr key={`${line.purpose ?? line.account_code}-${line.debit}-${line.credit}-${index}`} className="border-b border-gray-100 group">
									<td className="py-2 px-3">
										{isEditing ? (
											<JournalAccountSelect
												value={editedLine.account_code ?? line.account_code}
												onChange={(code, name) => {
													console.log("Account changed to:", code, name);
													setEditedLine({ ...editedLine, account_code: code, account_name: name });
												}}
											/>
										) : (
											<span className="whitespace-nowrap">
												<span className="font-mono text-xs text-gray-500">{line.account_code}</span> <span className="text-gray-900">{line.account_name}</span>
											</span>
										)}
									</td>
									<td className="py-2 px-3">
										{isEditing ? (
											<input
												type="text"
												value={editedLine.description ?? line.description}
												onChange={(e) => setEditedLine({ ...editedLine, description: e.target.value })}
												className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
											/>
										) : (
											<span className="text-gray-700">{line.description}</span>
										)}
									</td>
									<td className="py-2 px-3 text-right">
										{isEditing ? (
											<input
												type="number"
												step="0.01"
												min="0"
												value={editedLine.debit ?? line.debit}
												onChange={(e) => setEditedLine({ ...editedLine, debit: Number(e.target.value) })}
												className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded tabular-nums"
											/>
										) : (
											<span className="tabular-nums">{line.debit > 0 ? phpFormat(line.debit) : "—"}</span>
										)}
									</td>
									<td className="py-2 px-3 text-right">
										{isEditing ? (
											<input
												type="number"
												step="0.01"
												min="0"
												value={editedLine.credit ?? line.credit}
												onChange={(e) => setEditedLine({ ...editedLine, credit: Number(e.target.value) })}
												className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded tabular-nums"
											/>
										) : (
											<span className="tabular-nums">{line.credit > 0 ? phpFormat(line.credit) : "—"}</span>
										)}
									</td>
									<td className="py-2 px-3 text-right">
										{isEditing ? (
											<input
												type="text"
												value={editedLine.tax_code ?? line.tax_code ?? ""}
												onChange={(e) => setEditedLine({ ...editedLine, tax_code: e.target.value || null })}
												className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded"
											/>
										) : (
											<span className="text-xs text-gray-500">{line.tax_code ?? "—"}</span>
										)}
									</td>
									{isDraft && (
										<td className="py-2 px-3">
											{isEditing ? (
												<div className="flex justify-end gap-1">
													<button
														type="button"
														onClick={handleSaveEdit}
														className="p-1 text-green-600 hover:bg-green-50 rounded"
														title="Save"
														disabled={updateMutation.isPending}
													>
														<IconCheck size={16} stroke={2} />
													</button>
													<button
														type="button"
														onClick={handleCancelEdit}
														className="p-1 text-red-500 hover:bg-red-50 rounded"
														title="Cancel"
														disabled={updateMutation.isPending}
													>
														<IconX size={16} stroke={2} />
													</button>
												</div>
											) : (
												<div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
													<button
														type="button"
														onClick={() => handleEdit(index)}
														className="p-1 text-blue-600 hover:bg-blue-50 rounded"
														title="Edit"
														disabled={editingIndex !== null || isAdding}
													>
														<IconEdit size={16} stroke={2} />
													</button>
													<button
														type="button"
														onClick={() => handleDelete(index)}
														className="p-1 text-red-500 hover:bg-red-50 rounded"
														title="Delete"
														disabled={updateMutation.isPending || editingIndex !== null || isAdding}
													>
														<IconTrash size={16} stroke={2} />
													</button>
												</div>
											)}
										</td>
									)}
								</tr>
							);
						})}
						{isDraft && isAdding && (
							<tr className="border-b border-gray-100 bg-blue-50">
								<td className="py-2 px-3">
									<JournalAccountSelect value={newLine.account_code ?? ""} onChange={(code, name) => setNewLine({ ...newLine, account_code: code, account_name: name })} />
								</td>
								<td className="py-2 px-3">
									<input
										type="text"
										value={newLine.description ?? ""}
										onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
										placeholder="Description"
										className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
									/>
								</td>
								<td className="py-2 px-3">
									<input
										type="number"
										step="0.01"
										min="0"
										value={newLine.debit ?? 0}
										onChange={(e) => setNewLine({ ...newLine, debit: Number(e.target.value) })}
										placeholder="0.00"
										className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded tabular-nums"
									/>
								</td>
								<td className="py-2 px-3">
									<input
										type="number"
										step="0.01"
										min="0"
										value={newLine.credit ?? 0}
										onChange={(e) => setNewLine({ ...newLine, credit: Number(e.target.value) })}
										placeholder="0.00"
										className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded tabular-nums"
									/>
								</td>
								<td className="py-2 px-3">
									<input
										type="text"
										value={newLine.tax_code ?? ""}
										onChange={(e) => setNewLine({ ...newLine, tax_code: e.target.value || null })}
										placeholder="Tax code"
										className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded"
									/>
								</td>
								<td className="py-2 px-3">
									<div className="flex justify-end gap-1">
										<button
											type="button"
											onClick={handleAddLine}
											className="p-1 text-green-600 hover:bg-green-50 rounded"
											title="Save"
											disabled={updateMutation.isPending}
										>
											<IconCheck size={16} stroke={2} />
										</button>
										<button
											type="button"
											onClick={() => {
												setIsAdding(false);
												setNewLine({ account_code: "", account_name: "", description: "", debit: 0, credit: 0, tax_code: null, purpose: null });
											}}
											className="p-1 text-red-500 hover:bg-red-50 rounded"
											title="Cancel"
											disabled={updateMutation.isPending}
										>
											<IconX size={16} stroke={2} />
										</button>
									</div>
								</td>
							</tr>
						)}
					</tbody>
					<tfoot className="border-t-2 border-gray-300 font-semibold">
						<tr>
							<td colSpan={2} className="py-2 px-3 text-right text-xs uppercase tracking-wide text-gray-500">
								Totals
							</td>
							<td className="py-2 px-3 text-right tabular-nums">{phpFormat(data.total_debit)}</td>
							<td className="py-2 px-3 text-right tabular-nums">{phpFormat(data.total_credit)}</td>
							<td colSpan={2} />
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
}

function StatusBadge({ posted, booksDraft }: { posted: boolean; booksDraft?: boolean }) {
	if (posted) {
		return <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-800">Posted</span>;
	}
	if (booksDraft) {
		return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-800">Draft in Books</span>;
	}
	return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">Draft</span>;
}
